/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Submit from 'bundles/authentication-common/components/Submit';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { InvalidDomainSSOError } from 'bundles/authentication-common/components/sso/SSOError';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  email: string;
  magicLinkRedirect?: string;
  ssoError?: SSOError;
};

export type State = {
  magicLinkSent: boolean;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });

const styles = {
  container: css`
    display: grid;
    grid-gap: 0;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: var(--cds-spacing-50);

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  header: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  description: css`
    margin: var(--cds-spacing-300) 0;

    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-50) 0 var(--cds-spacing-200) 0;
    }
  `,
  buttonContainer: css`
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
    margin: var(--cds-spacing-300) 0 var(--cds-spacing-200) 0;
  `,
  navigation: css`
    margin: 0;
    display: flex;
    color: var(--cds-color-blue-800) !important;
    text-decoration: underline;
  `,
  navigationIcon: css`
    margin-right: 0.6rem;
  `,
  section: css`
    &:nth-child(1) {
      -ms-grid-row: 1;
    }

    &:nth-child(2) {
      -ms-grid-row: 2;
    }

    &:nth-child(3) {
      -ms-grid-row: 3;
    }

    &:nth-child(4) {
      -ms-grid-row: 4;
    }
  `,
  title: css`
    margin: 0;
  `,
  submit: css`
    margin-top: 0;
    margin-bottom: 0;
    width: 100%;
  `,
  spamText: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  boldEmail: css`
    font-weight: 700;
  `,
};

const MagicLink = ({ className, email, magicLinkRedirect, navigate, ssoError }: Props) => {
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { trackButton } = useTrackClickButton();
  const isInvalidDomainSSOError = ssoError ? ssoError instanceof InvalidDomainSSOError : false;

  const navigateToLogin = () => {
    if (isInvalidDomainSSOError) {
      navigate(AUTHENTICATION_MODAL_MODES.LOGIN, { clearAuthError: true });
    } else {
      navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
    }
  };

  const resendLink = () => {
    trackButton({ name: 'resend_magic_link' });
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });
    setMagicLinkSent(true);
  };

  return (
    <section className={className} css={styles.container} aria-live="polite">
      <header css={styles.section}>
        <Typography2 component="h2" variant="titleXSmall" css={styles.title} align="left">
          {_t('Check your inbox')}
        </Typography2>
      </header>
      <Typography2
        component="p"
        variant="bodyPrimary"
        color="supportText"
        css={[styles.description, styles.section]}
        align="left"
      >
        <FormattedMessage
          message={_t('We emailed you an login link to \n {email}. \n The link will expire shortly.')}
          email={<span css={styles.boldEmail}>{email}</span>}
        />
      </Typography2>
      {!isInvalidDomainSSOError ? (
        <TrackedSubmit
          css={[styles.submit, styles.section, styles.buttonContainer]}
          trackingName="resend_login_link_button"
          disabled={magicLinkSent}
          onClick={resendLink}
          secondary
        >
          {_t('Re-send email')}
        </TrackedSubmit>
      ) : null}
      <div css={styles.section}>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" css={styles.spamText} align="left">
          {_t("Can't find your link? Check your spam folder.")}
        </Typography2>

        <LinkButton css={styles.navigation} onClick={navigateToLogin}>
          <div css={styles.navigationIcon}>
            <SvgArrowLeft color="var(--cds-color-blue-700)" size={15} />
          </div>
          <Typography2 component="span" variant="bodySecondary" color="inherit" align="left">
            {_t('Back to Log in')}
          </Typography2>
        </LinkButton>
      </div>
    </section>
  );
};
export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'magic_link_confirm_page',
  },
}))(MagicLink);
